import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { createColumnHelper } from '@tanstack/react-table';
import { FileSpreadsheet, FileText, Loader2, Plus, Trash2, Pencil, Youtube, TriangleAlert, ExternalLink, Search } from 'lucide-react';
import { OrgPortalApi } from '@/services/org.api';
import { OrgPromotionApi } from '@/services/org-promotion.api';
import {
  CreateOrgLessonData,
  OrgLessonRow,
  OrgReportsApi,
  UpdateOrgLessonData,
} from '@/services/org-reports.api';
import { CreateLessonFileData, LessonFile } from '@/services/academic.api';
import { UploadApi } from '@/services/school.api';
import type { ExportFormat } from '@/services/school.api';
import { PageHeader } from '@/components/ui/PageHeader';
import { Field, Input, Select, Textarea } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { Badge } from '@/components/ui/Badge';
import { DataTable, useDataTable } from '@/components/data-table';

const columnHelper = createColumnHelper<OrgLessonRow>();

function errMsg(e: unknown): string {
  const anyE = e as any;
  return anyE?.response?.data?.error?.message ?? anyE?.message ?? 'Something went wrong. Please try again.';
}

// ───── School → Class → Section → Subject cascading picker (org tier) ───────
// Class/Section come from OrgPromotionApi.classesForSchool() — already built
// for Promote Student, reused as-is rather than duplicating a "classes for
// school" lookup. Subject is the one genuinely new lookup
// (OrgReportsApi.classSubjectsForClass).
function OrgClassSectionSubjectPicker({
  schoolId,
  classId,
  onClassChange,
  sectionId,
  onSectionChange,
  classSubjectId,
  onSubjectChange,
}: {
  schoolId: string;
  classId: string;
  onClassChange: (v: string) => void;
  sectionId: string;
  onSectionChange: (v: string) => void;
  classSubjectId: string;
  onSubjectChange: (v: string) => void;
}) {
  const { data: classes = [] } = useQuery({
    queryKey: ['org-promote-student-classes', schoolId],
    queryFn: () => OrgPromotionApi.classesForSchool(schoolId),
    enabled: !!schoolId,
  });
  const sections = classes.find((c) => c.id === classId)?.sections ?? [];

  const { data: subjects = [] } = useQuery({
    queryKey: ['org-lessons-class-subjects', schoolId, classId],
    queryFn: () => OrgReportsApi.classSubjectsForClass(schoolId, classId),
    enabled: !!schoolId && !!classId,
  });

  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
      <Field label="Class" required>
        <Select value={classId} onChange={(e) => onClassChange(e.target.value)} disabled={!schoolId}>
          <option value="">Select class</option>
          {classes.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </Select>
      </Field>
      <Field label="Section" required>
        <Select value={sectionId} onChange={(e) => onSectionChange(e.target.value)} disabled={!classId}>
          <option value="">Select section</option>
          {sections.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </Select>
      </Field>
      <Field label="Subject" required>
        <Select value={classSubjectId} onChange={(e) => onSubjectChange(e.target.value)} disabled={!classId}>
          <option value="">Select subject</option>
          {subjects.map((s) => (
            <option key={s.classSubjectId} value={s.classSubjectId}>
              {s.subjectName}
            </option>
          ))}
        </Select>
      </Field>
    </div>
  );
}

// ───── File attach/list widget — same UI as the school-tier one, callbacks
// wired to whichever org-scoped add/remove mutation the caller passes in. ───
function FilesManager({
  files,
  onAdd,
  adding,
  addError,
  onRemove,
  removingId,
}: {
  files: LessonFile[];
  onAdd: (data: CreateLessonFileData) => void;
  adding: boolean;
  addError?: unknown;
  onRemove: (fileId: string) => void;
  removingId?: string;
}) {
  const [fileType, setFileType] = useState<'document' | 'youtube'>('document');
  const [youtubeUrl, setYoutubeUrl] = useState('');
  const [title, setTitle] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | undefined>();

  async function handleDocumentPick(file: File) {
    setUploading(true);
    setUploadError(undefined);
    try {
      const res = await UploadApi.upload(file);
      onAdd({ fileType: 'document', fileUrl: res.url, title: title || res.name });
      setTitle('');
    } catch (e) {
      setUploadError(errMsg(e));
    } finally {
      setUploading(false);
    }
  }

  function handleAddYoutube() {
    if (!youtubeUrl.trim()) return;
    onAdd({ fileType: 'youtube', youtubeUrl: youtubeUrl.trim(), title: title || undefined });
    setYoutubeUrl('');
    setTitle('');
  }

  return (
    <div>
      {files.length === 0 ? (
        <p className="mb-2 text-xs text-slate-400">No files attached yet.</p>
      ) : (
        <ul className="mb-3 divide-y divide-slate-100">
          {files.map((f) => (
            <li key={f.id} className="flex items-center justify-between py-1.5">
              {f.note ? (
                <span className="flex items-center gap-2 text-sm text-amber-700" title={f.note}>
                  <TriangleAlert className="h-4 w-4 shrink-0" />
                  {f.title || 'Attachment'}
                  <span className="text-xs text-amber-600">— {f.note}</span>
                </span>
              ) : (
                <a
                  href={f.fileType === 'youtube' ? f.youtubeUrl ?? '#' : f.fileUrl ?? '#'}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center gap-2 text-sm text-brand-600 hover:underline"
                >
                  {f.fileType === 'youtube' ? <Youtube className="h-4 w-4 shrink-0" /> : <FileText className="h-4 w-4 shrink-0" />}
                  {f.title || (f.fileType === 'youtube' ? 'YouTube link' : 'Document')}
                  <ExternalLink className="h-3 w-3 shrink-0 text-slate-400" />
                </a>
              )}
              <button
                type="button"
                title="Remove"
                className="rounded p-1 text-slate-500 hover:bg-red-50 hover:text-red-600 disabled:opacity-50"
                disabled={removingId === f.id}
                onClick={() => onRemove(f.id)}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </li>
          ))}
        </ul>
      )}

      <div className="rounded-lg border border-dashed border-slate-300 p-3">
        <div className="mb-2 flex gap-1">
          <button
            type="button"
            className={`rounded px-2 py-1 text-xs ${fileType === 'document' ? 'bg-brand-600 text-white' : 'bg-slate-100 text-slate-600'}`}
            onClick={() => setFileType('document')}
          >
            Document
          </button>
          <button
            type="button"
            className={`rounded px-2 py-1 text-xs ${fileType === 'youtube' ? 'bg-brand-600 text-white' : 'bg-slate-100 text-slate-600'}`}
            onClick={() => setFileType('youtube')}
          >
            YouTube link
          </button>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Input
            placeholder="Title (optional)"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="!w-40 !py-1 text-sm"
          />
          {fileType === 'document' ? (
            <label className="btn-secondary cursor-pointer !py-1 text-xs">
              {uploading ? 'Uploading…' : 'Choose file'}
              <input
                type="file"
                className="hidden"
                disabled={uploading || adding}
                onChange={(e) => e.target.files?.[0] && handleDocumentPick(e.target.files[0])}
              />
            </label>
          ) : (
            <>
              <Input
                placeholder="https://youtube.com/watch?v=…"
                value={youtubeUrl}
                onChange={(e) => setYoutubeUrl(e.target.value)}
                className="!w-64 !py-1 text-sm"
              />
              <button
                type="button"
                className="btn-secondary flex items-center gap-1 !py-1 text-xs"
                disabled={!youtubeUrl.trim() || adding}
                onClick={handleAddYoutube}
              >
                <Plus className="h-3.5 w-3.5" /> Add
              </button>
            </>
          )}
        </div>
        {uploadError && <p className="mt-1 text-xs text-red-600">{uploadError}</p>}
        {addError ? <p className="mt-1 text-xs text-red-600">{errMsg(addError)}</p> : null}
      </div>
    </div>
  );
}

// ───── Per-school class/section/subject selection, keyed by schoolId ────────
type PerSchoolSelection = { classId: string; sectionId: string; classSubjectId: string };
const EMPTY_SELECTION: PerSchoolSelection = { classId: '', sectionId: '', classSubjectId: '' };

interface BulkLessonResult {
  schoolId: string;
  schoolName: string;
  ok: boolean;
  error?: string;
}

const ALL_SCHOOLS = '__all__';

// ───── Create lesson form — single School select, with "All Schools" as one
// of its options. Picking a specific school works exactly as before. Picking
// "All Schools" creates the same lesson (same name/description) in every
// school at once — each school still needs its own class/section/subject
// picked (those aren't shared across schools), so that case renders one
// cascading picker per school and fires one create call per school. ────────
function CreateLessonForm({ onDone }: { onDone: () => void }) {
  const qc = useQueryClient();

  // schoolId is either a real school id, '' (nothing picked), or ALL_SCHOOLS.
  const [schoolId, setSchoolId] = useState('');
  const [classId, setClassId] = useState('');
  const [sectionId, setSectionId] = useState('');
  const [classSubjectId, setClassSubjectId] = useState('');

  const [selections, setSelections] = useState<Record<string, PerSchoolSelection>>({});
  const [bulkResults, setBulkResults] = useState<BulkLessonResult[] | null>(null);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');

  const { data: schools = [] } = useQuery({
    queryKey: ['schools-for-lessons-report'],
    queryFn: async () => (await OrgPortalApi.listSchools({ limit: 100 })).items,
  });

  const isAllSchools = schoolId === ALL_SCHOOLS;

  useEffect(() => {
    setClassId('');
    setSectionId('');
    setClassSubjectId('');
    setBulkResults(null);
  }, [schoolId]);

  useEffect(() => {
    setSectionId('');
    setClassSubjectId('');
  }, [classId]);

  function updateSelection(id: string, patch: Partial<PerSchoolSelection>) {
    setSelections((prev) => ({ ...prev, [id]: { ...EMPTY_SELECTION, ...prev[id], ...patch } }));
  }

  const create = useMutation({
    mutationFn: () => {
      const data: CreateOrgLessonData = { schoolId, sectionId, classSubjectId, name, description: description || undefined };
      return OrgReportsApi.createLesson(data);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['org-lessons'] });
      onDone();
    },
  });

  const bulkCreate = useMutation({
    mutationFn: async (): Promise<BulkLessonResult[]> => {
      const settled = await Promise.allSettled(
        schools.map((school) => {
          const sel = selections[school.id] ?? EMPTY_SELECTION;
          const data: CreateOrgLessonData = {
            schoolId: school.id,
            sectionId: sel.sectionId,
            classSubjectId: sel.classSubjectId,
            name,
            description: description || undefined,
          };
          return OrgReportsApi.createLesson(data);
        }),
      );
      return settled.map((r, i) => ({
        schoolId: schools[i].id,
        schoolName: schools[i].name,
        ok: r.status === 'fulfilled',
        error: r.status === 'rejected' ? errMsg(r.reason) : undefined,
      }));
    },
    onSuccess: (results) => {
      qc.invalidateQueries({ queryKey: ['org-lessons'] });
      setBulkResults(results);
      if (results.every((r) => r.ok)) onDone();
    },
  });

  const bulkReady = schools.length > 0 && schools.every((s) => selections[s.id]?.sectionId && selections[s.id]?.classSubjectId);

  return (
    <div>
      <Field label="School" required className="mb-3">
        <Select value={schoolId} onChange={(e) => setSchoolId(e.target.value)}>
          <option value="">Select school</option>
          <option value={ALL_SCHOOLS}>All Schools</option>
          {schools.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </Select>
      </Field>

      {!isAllSchools ? (
        <OrgClassSectionSubjectPicker
          schoolId={schoolId}
          classId={classId}
          onClassChange={setClassId}
          sectionId={sectionId}
          onSectionChange={setSectionId}
          classSubjectId={classSubjectId}
          onSubjectChange={setClassSubjectId}
        />
      ) : (
        <div className="mb-3 max-h-72 space-y-3 overflow-y-auto pr-1">
          {schools.map((s) => {
            const sel = selections[s.id] ?? EMPTY_SELECTION;
            const result = bulkResults?.find((r) => r.schoolId === s.id);
            return (
              <div key={s.id} className="rounded-md border border-slate-200 p-3">
                <div className="mb-2 flex items-center justify-between text-sm font-medium text-slate-700">
                  {s.name}
                  {result && (
                    <span className={result.ok ? 'text-xs font-normal text-green-600' : 'text-xs font-normal text-red-600'}>
                      {result.ok ? 'Created' : result.error ?? 'Failed'}
                    </span>
                  )}
                </div>
                <OrgClassSectionSubjectPicker
                  schoolId={s.id}
                  classId={sel.classId}
                  onClassChange={(v) => updateSelection(s.id, { classId: v, sectionId: '', classSubjectId: '' })}
                  sectionId={sel.sectionId}
                  onSectionChange={(v) => updateSelection(s.id, { sectionId: v })}
                  classSubjectId={sel.classSubjectId}
                  onSubjectChange={(v) => updateSelection(s.id, { classSubjectId: v })}
                />
              </div>
            );
          })}
        </div>
      )}

      <Field label="Lesson name" required className="mt-3">
        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Chapter 3 — Photosynthesis" />
      </Field>
      <Field label="Description" className="mt-3">
        <Textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3} />
      </Field>
      {!isAllSchools && create.error && <p className="mt-2 text-xs text-red-600">{errMsg(create.error)}</p>}
      {isAllSchools && bulkResults && bulkResults.some((r) => !r.ok) && (
        <p className="mt-2 text-xs text-red-600">
          {bulkResults.filter((r) => !r.ok).length} of {bulkResults.length} school(s) failed — see details above.
        </p>
      )}
      <div className="mt-4 flex justify-end gap-2">
        <button className="btn-secondary" onClick={onDone}>
          Cancel
        </button>
        {!isAllSchools ? (
          <button
            className="btn-primary"
            disabled={!schoolId || !sectionId || !classSubjectId || !name.trim() || create.isPending}
            onClick={() => create.mutate()}
          >
            {create.isPending ? 'Creating…' : 'Create Lesson'}
          </button>
        ) : (
          <button className="btn-primary" disabled={!bulkReady || !name.trim() || bulkCreate.isPending} onClick={() => bulkCreate.mutate()}>
            {bulkCreate.isPending ? 'Creating…' : `Create for ${schools.length} school(s)`}
          </button>
        )}
      </div>
    </div>
  );
}

// ───── Edit lesson form ───────────────────────────────────────────────────
function EditLessonForm({ lesson, onDone }: { lesson: OrgLessonRow; onDone: () => void }) {
  const qc = useQueryClient();
  const [name, setName] = useState(lesson.lessonName);
  const [description, setDescription] = useState(lesson.description ?? '');

  const update = useMutation({
    mutationFn: () => {
      const data: UpdateOrgLessonData = { name, description: description || undefined };
      return OrgReportsApi.updateLesson(lesson.schoolId, lesson.lessonId, data);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['org-lessons'] });
      onDone();
    },
  });

  return (
    <div>
      <Field label="Lesson name" required>
        <Input value={name} onChange={(e) => setName(e.target.value)} />
      </Field>
      <Field label="Description" className="mt-3">
        <Textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3} />
      </Field>
      {update.error && <p className="mt-2 text-xs text-red-600">{errMsg(update.error)}</p>}
      <div className="mt-4 flex justify-end gap-2">
        <button className="btn-secondary" onClick={onDone}>
          Cancel
        </button>
        <button className="btn-primary" disabled={!name.trim() || update.isPending} onClick={() => update.mutate()}>
          {update.isPending ? 'Saving…' : 'Save'}
        </button>
      </div>
    </div>
  );
}

// ───── Manage-files detail modal ─────────────────────────────────────────────
function LessonDetail({ schoolId, schoolName, lessonId }: { schoolId: string; schoolName: string; lessonId: string }) {
  const qc = useQueryClient();
  const { data: detail } = useQuery({
    queryKey: ['org-lessons', schoolId, lessonId, 'detail'],
    queryFn: () => OrgReportsApi.lessonDetail(schoolId, lessonId),
  });

  const addFile = useMutation({
    mutationFn: (data: CreateLessonFileData) => OrgReportsApi.addLessonFile(schoolId, lessonId, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['org-lessons', schoolId, lessonId, 'detail'] });
      qc.invalidateQueries({ queryKey: ['org-lessons'] });
    },
  });
  const [removingId, setRemovingId] = useState<string | undefined>();
  const removeFile = useMutation({
    mutationFn: (fileId: string) => OrgReportsApi.removeLessonFile(schoolId, lessonId, fileId),
    onMutate: (fileId) => setRemovingId(fileId),
    onSettled: () => setRemovingId(undefined),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['org-lessons', schoolId, lessonId, 'detail'] });
      qc.invalidateQueries({ queryKey: ['org-lessons'] });
    },
  });

  if (!detail) return <p className="text-sm text-slate-500">Loading…</p>;

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500">
        <Badge tone="slate">{schoolName}</Badge>
        <Badge tone="blue">{detail.className}</Badge>
        <Badge tone="slate">{detail.sectionName}</Badge>
        <Badge tone="indigo">{detail.subjectName}</Badge>
      </div>
      {detail.description && <p className="text-sm text-slate-700">{detail.description}</p>}

      <div>
        <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Files</h4>
        <FilesManager
          files={detail.files}
          onAdd={(d) => addFile.mutate(d)}
          adding={addFile.isPending}
          addError={addFile.error}
          onRemove={(id) => removeFile.mutate(id)}
          removingId={removingId}
        />
      </div>
    </div>
  );
}

// ───── Page ────────────────────────────────────────────────────────────────
export function OrgLessonsPage() {
  const [schoolId, setSchoolId] = useState('');
  const [busy, setBusy] = useState<ExportFormat | null>(null);
  const [creating, setCreating] = useState(false);
  const [managing, setManaging] = useState<OrgLessonRow | null>(null);
  const [editing, setEditing] = useState<OrgLessonRow | null>(null);
  const [deleting, setDeleting] = useState<OrgLessonRow | null>(null);
  const qc = useQueryClient();
  const dt = useDataTable({ defaultLimit: 20 });

  useEffect(() => {
    dt.onPaginationChange((prev) => ({ ...prev, pageIndex: 0 }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schoolId]);

  const { data: schools = [] } = useQuery({
    queryKey: ['schools-for-lessons-report'],
    queryFn: async () => (await OrgPortalApi.listSchools({ limit: 100 })).items,
  });

  const filterParams = { schoolId: schoolId || undefined };

  const { data, isFetching } = useQuery({
    queryKey: ['org-lessons', dt.params, filterParams],
    queryFn: () => OrgReportsApi.lessons({ ...dt.params, ...filterParams }),
    placeholderData: keepPreviousData,
  });

  const remove = useMutation({
    mutationFn: (r: OrgLessonRow) => OrgReportsApi.removeLesson(r.schoolId, r.lessonId),
    onSuccess: () => {
      setDeleting(null);
      qc.invalidateQueries({ queryKey: ['org-lessons'] });
    },
  });

  const runExport = async (format: ExportFormat) => {
    if (busy) return;
    setBusy(format);
    try {
      await OrgReportsApi.exportLessons(format, { ...filterParams, search: dt.params.search });
    } finally {
      setBusy(null);
    }
  };

  const COLUMNS = [
    columnHelper.display({
      id: 'no',
      header: 'No.',
      enableSorting: false,
      cell: (info) => (
        <span className="text-slate-400">{dt.pagination.pageIndex * dt.pagination.pageSize + info.row.index + 1}</span>
      ),
    }),
    columnHelper.accessor('schoolName', { header: 'School' }),
    columnHelper.display({
      id: 'lessonName',
      header: 'Lesson',
      cell: (ctx) => (
        <button type="button" className="text-brand-600 hover:underline" onClick={() => setManaging(ctx.row.original)}>
          {ctx.row.original.lessonName}
        </button>
      ),
    }),
    columnHelper.accessor('className', { header: 'Class' }),
    columnHelper.accessor('sectionName', { header: 'Section' }),
    columnHelper.accessor('subjectName', { header: 'Subject' }),
    columnHelper.display({
      id: 'fileCount',
      header: 'Files',
      enableSorting: false,
      cell: (ctx) => <Badge tone={ctx.row.original.fileCount > 0 ? 'blue' : 'slate'}>{ctx.row.original.fileCount}</Badge>,
    }),
    columnHelper.display({
      id: 'topicCount',
      header: 'Topics',
      enableSorting: false,
      cell: (ctx) => <Badge tone={ctx.row.original.topicCount > 0 ? 'blue' : 'slate'}>{ctx.row.original.topicCount}</Badge>,
    }),
    columnHelper.display({
      id: 'action',
      header: 'Action',
      enableSorting: false,
      cell: (ctx) => (
        <span className="inline-flex items-center gap-2">
          <button
            type="button"
            title="Edit"
            className="rounded p-1 text-slate-500 hover:bg-slate-100 hover:text-brand-700"
            onClick={() => setEditing(ctx.row.original)}
          >
            <Pencil className="h-4 w-4" />
          </button>
          <button
            type="button"
            title="Delete"
            className="rounded p-1 text-slate-500 hover:bg-red-50 hover:text-red-600"
            onClick={() => setDeleting(ctx.row.original)}
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </span>
      ),
    }),
  ];

  return (
    <>
      <PageHeader
        title="Lessons"
        description="Real lessons across your organization's schools."
        actions={
          <div className="flex items-center gap-2">
            <button className="btn-primary flex items-center gap-1.5" onClick={() => setCreating(true)}>
              <Plus className="h-4 w-4" /> Create Lesson
            </button>
            <div className="inline-flex overflow-hidden rounded-md border border-slate-200">
              <button
                type="button"
                className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-sm text-slate-600 hover:bg-slate-50 disabled:opacity-50"
                onClick={() => runExport('xlsx')}
                disabled={!!busy}
                title="Export to Excel"
              >
                {busy === 'xlsx' ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileSpreadsheet className="h-4 w-4 text-green-600" />}
                Excel
              </button>
              <div className="w-px bg-slate-200" />
              <button
                type="button"
                className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-sm text-slate-600 hover:bg-slate-50 disabled:opacity-50"
                onClick={() => runExport('pdf')}
                disabled={!!busy}
                title="Export to PDF"
              >
                {busy === 'pdf' ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4 text-red-600" />}
                PDF
              </button>
            </div>
          </div>
        }
      />

      {data?.truncated && (
        <div className="mb-4 rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-700">
          This organization has more lessons than this report can show at once — results are truncated.
        </div>
      )}

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
          <Input
            value={dt.searchInput}
            onChange={(e) => dt.setSearchInput(e.target.value)}
            placeholder="Search by lesson name"
            className="w-72 pl-9"
          />
        </div>
        <Select className="!w-56" value={schoolId} onChange={(e) => setSchoolId(e.target.value)}>
          <option value="">All schools</option>
          {schools.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </Select>
      </div>

      <DataTable
        columns={COLUMNS}
        result={data}
        state={dt}
        loading={isFetching}
        emptyMessage="No lessons found."
        getRowId={(row) => `${row.schoolId}:${row.lessonId}`}
      />

      <Modal open={creating} onClose={() => setCreating(false)} title="Create Lesson" size="xl">
        <CreateLessonForm onDone={() => setCreating(false)} />
      </Modal>

      <Modal open={!!editing} onClose={() => setEditing(null)} title="Edit Lesson" size="md">
        {editing && <EditLessonForm lesson={editing} onDone={() => setEditing(null)} />}
      </Modal>

      <Modal open={!!managing} onClose={() => setManaging(null)} title={managing?.lessonName ?? 'Lesson'} size="lg">
        {managing && <LessonDetail schoolId={managing.schoolId} schoolName={managing.schoolName} lessonId={managing.lessonId} />}
      </Modal>

      <ConfirmDialog
        open={!!deleting}
        onClose={() => setDeleting(null)}
        onConfirm={() => remove.mutate(deleting!)}
        title="Delete lesson?"
        message={
          remove.error
            ? errMsg(remove.error)
            : `This permanently deletes "${deleting?.lessonName}", all its topics, and every attached file. This cannot be undone.`
        }
        confirmText="Delete"
        destructive
        loading={remove.isPending}
      />
    </>
  );
}
